import { inject, ChangeDetectionStrategy, Component, OnInit, computed, model, output, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { EscolaService } from '@/core/api/sicom-api/registro/medico/escola/escola.service';
import { MedicoEscolasResponse } from '@/core/api/sicom-api/registro/medico/escola/escola.model';
import { EscolaPosResponse } from '@/core/api/sicom-api/registro/medico/escola/escola-pos.model';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
export interface EscolaRow {
    uf: string;
    nome: string;
    nomeAnterior?: string | null;
    municipio?: string | null;
    /** Identificador retornado pela API (Graduação: idSolicitante; Pós: pode ser preenchido com coIes) */
    id?: number | null;
    // Para Pós-Graduação: código da IES retornado pelo endpoint de escolas de pós
    coIes?: number | null;
}

export interface PesquisaEscolaResult extends EscolaRow {}

@Component({
    selector: 'app-pesquisar-escola-dialog',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, Dialog, Button, InputText, TableModule, PaginatorModule, SelectEstado],
    templateUrl: './pesquisar-escola.html',
    styleUrl: './pesquisar-escola.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Diálogo de pesquisa de escola (graduação/pós). */
export class PesquisarEscolaDialogComponent implements OnInit {
    private readonly logger = inject(LoggerService);
    // Controle de visibilidade (padrão do sistema com model())
    showDialog = model.required<boolean>();

    // Tipo da pesquisa: 'GRAD' | 'POS' (default GRAD)
    tipo = model<'GRAD' | 'POS'>('GRAD');

    // Estado do formulário (simples)
    nome = model<string>('');
    // Usa o componente de UF padrão do sistema, iniciando em 'BR'
    estado = model<string>('BR');
    municipio = model<string>('');

    // Removido: lista local de estados, pois usamos o componente padrão do sistema

    // Resultados da busca
    resultados = signal<EscolaRow[]>([]);
    selecionado = signal<EscolaRow | null>(null);
    total = computed(() => this.resultados().length);
    carregando = signal<boolean>(false);

    // Paginação (padrão do sistema: paginator externo)
    first = signal<number>(0);
    rows = signal<number>(10);
    displayed = computed<EscolaRow[]>(() => {
        const start = this.first();
        const end = start + this.rows();
        return this.resultados().slice(start, end);
    });

    // Eventos
    saved = output<PesquisaEscolaResult>();
    canceled = output<void>();

    constructor(private escolaService: EscolaService) {}

    ngOnInit(): void {}

    hide(): void {
        this.showDialog.set(false);
    }

    onCancelar(): void {
        this.canceled.emit();
        this.hide();
    }

    onConfirmar(): void {
        const row = this.selecionado();
        if (row) {
            this.saved.emit({ ...row });
            this.hide();
        }
    }

    localizar(): void {
        const uf = this.estado() ?? 'BR';
        const termo = this.nome() || undefined;
        // Observação: Campo de município do diálogo é texto livre; endpoint espera idMunicipio (number).
        // Integração mínima: não enviar idMunicipio até haver seleção assistida de municípios.

        this.carregando.set(true);

        const isPos = this.tipo() === 'POS';

        if (isPos) {
            this.escolaService.listarPos(termo, uf).subscribe({
                next: (lista: EscolaPosResponse[]) => {
                    const rows: EscolaRow[] = (lista || []).map((e) => ({
                        uf: e?.sgUfOferta || '',
                        nome: e?.nmEscola || '',
                        nomeAnterior: e?.sgEscola || null,
                        municipio: null,
                        coIes: e?.coIes ?? null,
                        id: e?.coIes ?? null
                    }));
                    this.resultados.set(rows);
                    this.selecionado.set(null);
                    this.first.set(0);
                    this.carregando.set(false);
                },
                error: (err: unknown) => {
                    this.logger.error('Erro ao localizar escolas (POS)', err);
                    this.resultados.set([]);
                    this.selecionado.set(null);
                    this.first.set(0);
                    this.carregando.set(false);
                }
            });
        } else {
            this.escolaService.listar(uf, termo, undefined).subscribe({
                next: (lista: MedicoEscolasResponse[]) => {
                    const rows: EscolaRow[] = (lista || []).map((e) => ({
                        uf: e?.sgUf || '',
                        nome: e?.nmSolicitante || '',
                        nomeAnterior: e?.nmAnterior || null,
                        municipio: null,
                        id: e?.idSolicitante ?? null
                    }));
                    this.resultados.set(rows);
                    this.selecionado.set(null);
                    this.first.set(0);
                    this.carregando.set(false);
                },
                error: (err: unknown) => {
                    this.logger.error('Erro ao localizar escolas (GRAD)', err);
                    this.resultados.set([]);
                    this.selecionado.set(null);
                    this.first.set(0);
                    this.carregando.set(false);
                }
            });
        }
    }

    // Helper para template
    isPos(): boolean {
        return this.tipo() === 'POS';
    }

    limpar(): void {
        this.nome.set('');
        this.estado.set('BR');
        this.municipio.set('');
        this.resultados.set([]);
        this.selecionado.set(null);
    }

    onPageChange(event: PaginatorState): void {
        const first = event.first ?? 0;
        const rows = event.rows ?? this.rows();
        this.first.set(first);
        this.rows.set(rows);
    }
}
